import { CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  TextInput as RNTextInput,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Menu, Portal } from 'react-native-paper';
import { DatePickerModal } from 'react-native-paper-dates';
import { Colors } from '../constants/Colors';
import { menuContentStyle } from '../constants/Styles';
import { TextReg } from './StyledText';

type Props = {
  label: string;
  showLabel?: boolean;
  value?: Date;
  onChange: (date: Date) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  minimumDate?: Date;
  maximumDate?: Date;
  testID?: string;
};

// Generate time options at 30-minute intervals using Luxon
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = (i % 2) * 30;
  const dt = DateTime.fromObject({ hour: hours, minute: minutes });
  return {
    label: dt.toFormat('h:mm a'),
    hours,
    minutes,
  };
});

const formatTime = (hours: number, minutes: number): string => {
  return DateTime.fromObject({ hour: hours, minute: minutes }).toFormat(
    'h:mm a',
  );
};

const parseTimeString = (
  timeStr: string,
): { hours: number; minutes: number } | null => {
  // Try multiple formats
  const formats = ['h:mm a', 'h:mma', 'H:mm', 'h a', 'ha'];
  for (const fmt of formats) {
    const parsed = DateTime.fromFormat(timeStr.trim(), fmt);
    if (parsed.isValid) {
      return { hours: parsed.hour, minutes: parsed.minute };
    }
  }
  return null;
};

export function DateTimeInput({
  label,
  showLabel = true,
  value,
  onChange,
  disabled = false,
  error = false,
  minimumDate,
  maximumDate,
  testID,
}: Props) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimeMenu, setShowTimeMenu] = useState(false);
  const [timeInputValue, setTimeInputValue] = useState('');
  const scrollViewRef = useRef<ScrollView>(null);

  // Get the displayed time (from value only, null if time not yet selected)
  const displayedTime = useMemo(() => {
    if (value && !(value.getHours() === 0 && value.getMinutes() === 0)) {
      return { hours: value.getHours(), minutes: value.getMinutes() };
    }
    return null;
  }, [value]);

  // Get the current selected time index for auto-scroll
  const selectedTimeIndex = useMemo(() => {
    if (!displayedTime) return -1;
    const { hours, minutes } = displayedTime;
    // Round to nearest 30-minute interval for finding index
    const roundedMinutes = minutes >= 30 ? 30 : 0;
    return TIME_OPTIONS.findIndex(
      (opt) => opt.hours === hours && opt.minutes === roundedMinutes,
    );
  }, [displayedTime]);

  // Auto-scroll to selected time when menu opens
  useEffect(() => {
    if (showTimeMenu && scrollViewRef.current && selectedTimeIndex >= 0) {
      // Each menu item is approximately 48px tall
      const itemHeight = 48;
      const scrollOffset = Math.max(0, selectedTimeIndex * itemHeight - 100);
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: scrollOffset, animated: false });
      }, 50);
    }
  }, [showTimeMenu, selectedTimeIndex]);

  // Format date for display (e.g., "Fri, Dec 19")
  const formatDateDisplay = (): string => {
    if (!value) return 'Fri, Dec 19';
    return value.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get current time display - show placeholder if no value or if time is midnight (not yet selected)
  const hasTimeSelected =
    value && !(value.getHours() === 0 && value.getMinutes() === 0);
  const timeDisplay = useMemo(() => {
    if (!value || !hasTimeSelected) {
      return '4:00 PM';
    }
    return formatTime(value.getHours(), value.getMinutes());
  }, [value, hasTimeSelected]);

  const handleDateChange = ({ date }: { date: Date | undefined }) => {
    if (date) {
      const newDate = new Date(date);
      if (value) {
        // Preserve existing time
        newDate.setHours(value.getHours(), value.getMinutes(), 0, 0);
      } else {
        // No default time - keep midnight, user must select time
        newDate.setHours(0, 0, 0, 0);
      }
      onChange(newDate);
      setShowDatePicker(false);
    }
  };

  const handleDateConfirm = ({ date }: { date?: Date }) => {
    if (date) {
      handleDateChange({ date });
    } else {
      setShowDatePicker(false);
    }
  };

  const handleTimeSelect = (hours: number, minutes: number) => {
    const newDate = value ? new Date(value) : new Date();
    newDate.setHours(hours, minutes, 0, 0);
    onChange(newDate);
    setShowTimeMenu(false);
  };

  const handleTimeInputChange = (text: string) => {
    setTimeInputValue(text);
  };

  const handleTimeInputSubmit = () => {
    if (!timeInputValue.trim()) {
      setShowTimeMenu(false);
      return;
    }

    const parsed = parseTimeString(timeInputValue);
    if (parsed) {
      handleTimeSelect(parsed.hours, parsed.minutes);
    } else {
      setShowTimeMenu(false);
    }
    setTimeInputValue('');
  };

  const handleDatePress = () => {
    if (!disabled) {
      setShowDatePicker(true);
    }
  };

  const handleTimeFieldPress = () => {
    if (!disabled) {
      setTimeInputValue(timeDisplay);
      setShowTimeMenu(true);
    }
  };

  return (
    <View style={styles.wrapper}>
      {showLabel && <TextReg style={styles.label}>{label}</TextReg>}
      <View style={[styles.container, error && styles.containerError]}>
        {/* Date Section */}
        <Pressable
          onPress={handleDatePress}
          disabled={disabled}
          style={styles.dateSection}
          testID={testID ? `${testID}-date` : undefined}
        >
          <TextReg style={[styles.dateText, !value && styles.placeholderText]}>
            {formatDateDisplay()}
          </TextReg>
        </Pressable>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Time Section */}
        <Menu
          visible={showTimeMenu}
          onDismiss={() => setShowTimeMenu(false)}
          anchor={
            <Pressable
              onPress={handleTimeFieldPress}
              disabled={disabled}
              style={styles.timeSection}
              testID={testID ? `${testID}-time` : undefined}
            >
              <TextReg
                style={[
                  styles.timeText,
                  !hasTimeSelected && styles.placeholderText,
                ]}
              >
                {timeDisplay}
              </TextReg>
            </Pressable>
          }
          contentStyle={menuContentStyle}
        >
          <View style={styles.timeInputContainer}>
            <RNTextInput
              value={timeInputValue}
              onChangeText={handleTimeInputChange}
              onSubmitEditing={handleTimeInputSubmit}
              style={styles.timeInput}
              placeholder="Time"
              placeholderTextColor={Colors.opacity50}
            />
          </View>
          <ScrollView ref={scrollViewRef} style={styles.timeScrollView}>
            {TIME_OPTIONS.map((option) => {
              const isSelected =
                displayedTime !== null &&
                displayedTime.hours === option.hours &&
                displayedTime.minutes === option.minutes;
              return (
                <Pressable
                  key={`${option.hours}-${option.minutes}`}
                  onPress={() => handleTimeSelect(option.hours, option.minutes)}
                  style={styles.menuItem}
                >
                  <TextReg
                    style={[
                      styles.menuItemTitle,
                      isSelected && styles.menuItemTitleSelected,
                    ]}
                  >
                    {option.label}
                  </TextReg>
                  {isSelected && (
                    <HugeiconsIcon
                      icon={CheckmarkCircle01Icon}
                      color={Colors.purple1}
                      size={18}
                    />
                  )}
                </Pressable>
              );
            })}
          </ScrollView>
        </Menu>

        {/* Date Picker Modal */}
        <Portal>
          <DatePickerModal
            locale="en"
            mode="single"
            visible={showDatePicker}
            onDismiss={() => setShowDatePicker(false)}
            date={value}
            onChange={handleDateChange}
            onConfirm={handleDateConfirm}
            validRange={{
              startDate: minimumDate,
              endDate: maximumDate,
            }}
            presentationStyle="pageSheet"
          />
        </Portal>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    gap: 4,
  },
  label: {},
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.opacity05,
    borderRadius: 30,
    overflow: 'hidden',
  },
  containerError: {
    borderWidth: 1,
    borderColor: Colors.error,
  },
  dateSection: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  dateText: {
    fontSize: 16,
    color: Colors.text,
  },
  placeholderText: {
    color: Colors.opacity50,
  },
  divider: {
    width: 1,
    height: '60%',
    backgroundColor: Colors.opacity20,
  },
  timeSection: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    minWidth: 100,
  },
  timeText: {
    fontSize: 16,
    color: Colors.text,
    textAlign: 'center',
  },
  timeInputContainer: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.opacity20,
  },
  timeInput: {
    fontSize: 14,
    color: Colors.text,
    backgroundColor: Colors.opacity10,
    borderRadius: 30,
    paddingHorizontal: 12,
    paddingVertical: 8,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  timeScrollView: {
    maxHeight: 280,
  },
  menuItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  menuItemTitle: {
    color: Colors.text,
  },
  menuItemTitleSelected: {
    color: Colors.purple1,
  },
});
